// Pure logic for the account card.
//
// Lives outside the component so it can be tested without a DOM, matching
// authForm.js and prescriptionForm.js. The component keeps rendering and state;
// every decision about what is valid, and what gets sent, is here.

import { MIN_PASSWORD_LENGTH } from './authForm';

const asText = (value) => String(value ?? '').trim();

/** Blank starting state for the account form. */
export const EMPTY_ACCOUNT_FORM = {
  username: '',
  currentPassword: '',
  password: '',
  confirmPassword: '',
};

/**
 * Validate the account form, returning field-keyed errors.
 *
 * The password fields are optional as a group: leaving all three blank means
 * "not changing my password", and filling any of them means all three are
 * required. Treating them individually would ask someone who typed a new
 * password twice why they had not also typed their current one, one field at a
 * time.
 *
 * @param {object} form
 * @param {object} current the profile as loaded, used to spot a no-op
 * @returns {Object<string, string>} empty when the form is valid
 */
export function validateAccountForm(form = {}, current = {}) {
  const errors = {};

  const username = asText(form.username);
  if (username === '') {
    errors.username = 'Choose a username.';
  }

  // Not trimmed: leading and trailing spaces are legitimate password
  // characters, and stripping them would make the password that gets set differ
  // from the one that was typed.
  const currentPassword = String(form.currentPassword ?? '');
  const password = String(form.password ?? '');
  const confirmPassword = String(form.confirmPassword ?? '');

  if (currentPassword === '' && password === '' && confirmPassword === '') {
    return errors;
  }

  if (currentPassword === '') {
    errors.currentPassword = 'Enter your current password to set a new one.';
  }
  if (password === '') {
    errors.password = 'Choose a new password.';
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (confirmPassword === '') {
    errors.confirmPassword = 'Repeat the new password.';
  } else if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
}

/**
 * The request body for a valid form, or the errors that stop it.
 *
 * Only what actually changed is sent, and this is the half of "absent is not
 * empty" that lives in the client: the API treats an omitted field as "leave it
 * alone", so sending every field on every save would mean a request that reads
 * as a full overwrite even when nothing but the username moved.
 *
 * Returns `{ body: null }` when the form is valid and nothing changed, so the
 * caller can say so rather than posting a no-op.
 */
export function buildAccountRequest(form, current = {}) {
  const errors = validateAccountForm(form, current);
  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  const body = {};

  const username = asText(form.username);
  if (username !== asText(current.username)) {
    body.username = username;
  }

  const password = String(form.password ?? '');
  if (password !== '') {
    body.password = password;
    body.currentPassword = String(form.currentPassword ?? '');
  }

  return { body: Object.keys(body).length > 0 ? body : null };
}

/**
 * Whether a save will end this session's siblings.
 *
 * A password change evicts every other token (AUTH-26), which is worth saying
 * before it happens rather than leaving somebody to discover their other device
 * has signed itself out.
 */
export function signsOutOtherDevices(body) {
  return Boolean(body && body.password);
}
